import React, { useEffect, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Star, ShieldCheck, Lock, CreditCard, Download, Brain, Zap, FileText, ChevronRight } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { FAQItem } from '../components/FAQItem';
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}
const META_PIXEL_ID = '1167446325501718';
export function SalesPage() {
  // Meta Pixel: inject the base code once and track PageView
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.fbq) {
      window.fbq('track', 'PageView');
      return;
    }
    // Initialize the fbq stub (mirrors the official Meta Pixel snippet)
    const n: any = window.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    // Inject the loader script. Append to <head> (always present) instead of
    // relying on an existing <script> node, which may not exist in this bundle.
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const target = document.head || document.documentElement;
    target.appendChild(script);
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }, []);
  const trackCheckout = () => {
    window.fbq?.('track', 'InitiateCheckout');
  };
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="min-h-screen bg-[#09090b] text-zinc-50 selection:bg-orange-500/30" data-id="element-21">
      {/* Meta Pixel noscript fallback */}
      <noscript data-id="element-22">
        <img height="1" width="1" style={{
        display: 'none'
      }} alt="" src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`} data-id="element-23" />
      </noscript>

      {/* HEADER / HERO */}
      <header className="relative pt-8 pb-12 md:pt-10 md:pb-16 overflow-hidden" data-id="element-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(234,88,12,0.15),transparent_50%)] pointer-events-none" data-id="element-25" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-26">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center space-x-2 bg-zinc-900/80 border border-zinc-800 rounded-full px-4 py-1.5 mb-3" data-id="element-27">
            <Brain className="w-4 h-4 text-orange-500" data-id="element-28" />
            <span className="text-sm font-semibold tracking-wide text-zinc-300 uppercase" data-id="element-29">
              Focused Mindset
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-[1.15]" data-id="element-30">
            Your child's entire childhood of <br className="hidden md:block" data-id="element-31" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600" data-id="element-32">
              brain training solved.
            </span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="my-4 max-w-[220px] sm:max-w-[240px] mx-auto relative" data-id="element-33">
            <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-3xl opacity-40 pointer-events-none" data-id="element-34"></div>
            <img src="https://cdn.magicpatterns.com/uploads/61GF88ynKGgzpUnazAfbJE/Diseno_sin_titulo_(17).png" alt="Kit The Focused Mindset Product Bundle" className="relative w-full object-contain drop-shadow-2xl mx-auto" data-id="element-35" />
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-base md:text-lg mb-6 max-w-2xl mx-auto text-[#FDFDFF]" data-id="element-36">
            Open it, print it, and watch your child thrive — and reclaim your
            evenings for good.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex flex-nowrap justify-center items-center gap-1.5 sm:gap-2 mb-12" data-id="element-37">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-zinc-300 bg-zinc-900/50 px-2 py-1.5 rounded-full border border-zinc-800 whitespace-nowrap flex-shrink-0" data-id="element-38">
              <FileText className="w-3 h-3 text-orange-500 flex-shrink-0" data-id="element-39" />
              <span className="text-[#FAFAFC]" data-id="element-40">100% PDF</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-zinc-300 bg-zinc-900/50 px-2 py-1.5 rounded-full border border-zinc-800 whitespace-nowrap flex-shrink-0" data-id="element-41">
              <Zap className="w-3 h-3 text-orange-500 flex-shrink-0" data-id="element-42" />
              <span className="text-[#F8F8FC]" data-id="element-43">Instant Access</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-zinc-300 bg-zinc-900/50 px-2 py-1.5 rounded-full border border-zinc-800 whitespace-nowrap flex-shrink-0" data-id="element-44">
              <Brain className="w-3 h-3 text-orange-500 flex-shrink-0" data-id="element-45" />
              <span className="text-[#FFFFFF]" data-id="element-46">Built for Real Family Life</span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} data-id="element-47">
            <button onClick={scrollToOffer} className="group relative inline-flex items-center justify-center px-6 py-3.5 font-bold text-white bg-orange-600 rounded-full overflow-hidden transition-all hover:scale-105 hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-zinc-900 text-sm md:text-base w-full md:w-auto whitespace-nowrap shadow-[0_0_40px_rgba(234,88,12,0.4)]" data-id="element-48">
              <span className="mr-2" data-id="element-49">I WANT FOCUSED MINDSET NOW</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-50" />
            </button>

            <div className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-zinc-500" data-id="element-51">
              <span className="flex items-center text-[#FEFEFF]" data-id="element-52">
                🛡️ <span className="ml-1" data-id="element-53">Satisfied or Refunded in 7 Days</span>
              </span>
              <span className="flex items-center text-[#FEFEFF]" data-id="element-54">
                🔒 <span className="ml-1" data-id="element-55">Secure Payment</span>
              </span>
              <span className="flex items-center text-[#FFFFFF]" data-id="element-56">
                💳 <span className="ml-1" data-id="element-57">One-time Payment</span>
              </span>
            </div>
          </motion.div>
        </div>
      </header>

      {/* SOCIAL PROOF STATS */}
      <section className="border-y border-zinc-800 bg-zinc-900/30 py-8" data-id="element-58">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-59">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-zinc-800/50" data-id="element-60">
            <div data-id="element-61">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1" data-id="element-62">
                +1,000
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wider" data-id="element-63">
                Families trust us
              </div>
            </div>
            <div data-id="element-64">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1" data-id="element-65">
                6
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wider" data-id="element-66">
                Volumes Included
              </div>
            </div>
            <div data-id="element-67">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center justify-center" data-id="element-68">
                4.9/5{' '}
                <Star className="w-5 h-5 text-orange-500 fill-orange-500 ml-1" data-id="element-69" />
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wider" data-id="element-70">
                Rating
              </div>
            </div>
            <div data-id="element-71">
              <div className="text-2xl md:text-3xl font-bold text-white mb-1" data-id="element-72">
                🌍
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wider" data-id="element-73">
                Families Worldwide
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHORT TESTIMONIALS */}
      <section className="py-12 bg-[#09090b]" data-id="element-74">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-75">
          <div className="grid md:grid-cols-3 gap-6" data-id="element-76">
            {[{
            init: 'JM',
            text: 'My son went from 3 minutes of focus to 20-minute sessions on his own — in just 14 days.',
            author: 'Jessica M., Texas',
            role: 'Mom of a 7-year-old'
          }, {
            init: 'AK',
            text: 'After 2 weeks she pushes through every challenge without quitting.',
            author: 'Amanda K., Florida',
            role: 'Mom of an 8-year-old'
          }, {
            init: 'RT',
            text: 'By day three my kids were reminding ME to print the activity.',
            author: 'Rachel T., California',
            role: 'Mom of two ages 6 and 9'
          }].map((t, i) => <motion.div key={i} {...fadeIn} className="glass-panel p-6 rounded-2xl relative" data-id="element-77">
                <div className="flex items-center space-x-1 mb-4" data-id="element-78">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-orange-500 fill-orange-500 bg-[#090909]" data-id="element-79" />)}
                </div>
                <p className="text-zinc-300 mb-6 italic" data-id="element-80">"{t.text}"</p>
                <div className="flex items-center space-x-3 mt-auto" data-id="element-81">
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center font-bold text-orange-500" data-id="element-82">
                    {t.init}
                  </div>
                  <div data-id="element-83">
                    <div className="font-semibold text-white text-sm" data-id="element-84">
                      {t.author}
                    </div>
                    <div className="text-xs text-zinc-500" data-id="element-85">{t.role}</div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-16 bg-zinc-950 relative border-t border-zinc-900" data-id="element-86">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-87">
          <motion.div {...fadeIn} className="text-center mb-12" data-id="element-88">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-89">
              If you are a parent of a child between 3 and 12, you know this
              feeling all too well:
            </h2>
          </motion.div>

          <div className="space-y-4 mb-10" data-id="element-90">
            {['Evenings stolen by screens while you feel completely powerless.', 'Homework sessions that turn into emotional war zones every night.', 'The guilt of handing over the iPad just to get five minutes of peace.', 'Activity kits that lasted 3 days before gathering dust on the shelf.', 'The quiet fear that your child is falling behind at school.', 'Exhausting yourself searching Pinterest every Sunday for ideas that never happen.'].map((pain, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex items-start space-x-4 bg-zinc-900/50 p-4 rounded-xl border border-zinc-800/50" data-id="element-91">
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-92" />
                <span className="text-lg text-zinc-300" data-id="element-93">{pain}</span>
              </motion.div>)}
          </div>

          <motion.div {...fadeIn} className="text-center" data-id="element-94">
            <h3 className="text-2xl md:text-3xl font-bold text-orange-500" data-id="element-95">
              How many evenings have you lost feeling helpless — while their
              childhood slips by?
            </h3>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION / PRODUCT INTRO */}
      <section className="py-16 bg-[#09090b] relative overflow-hidden" data-id="element-96">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-orange-600/5 rounded-full blur-3xl pointer-events-none" data-id="element-97"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-98">
          <motion.div {...fadeIn} data-id="element-99">
            <div className="inline-flex items-center space-x-2 mb-6" data-id="element-100">
              <Brain className="w-6 h-6 text-orange-500" data-id="element-101" />
              <span className="text-xl font-bold tracking-widest text-zinc-100 uppercase" data-id="element-102">
                Focused Mindset
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-103">
              KIT THE FOCUSED MINDSET™ <br data-id="element-104" />
              <span className="text-zinc-400 text-3xl md:text-4xl" data-id="element-105">
                The Ultimate Family Brain Training Bundle
              </span>
            </h2>

            <p className="text-xl text-zinc-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-106">
              1 complete system built around 6 developmental volumes. 300+
              ready-to-use printable activities. 5 bonus guides included.
              Everything ready to print today.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center text-left" data-id="element-107">
            <motion.div {...fadeIn} className="space-y-6" data-id="element-108">
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center" data-id="element-109">
                <Brain className="w-6 h-6 text-orange-500 mr-3" data-id="element-110" />6
                DEVELOPMENTAL VOLUMES — ALL IN ONE SYSTEM
              </h3>

              {['Built on proven cognitive development principles', 'Clear progressions for every age and skill level', 'Activities designed for 10 minutes per session', 'Scalable for one child or your entire family'].map((benefit, i) => <div key={i} className="flex items-start space-x-4" data-id="element-111">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" data-id="element-112" />
                  <span className="text-lg text-zinc-300" data-id="element-113">{benefit}</span>
                </div>)}
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative max-w-sm mx-auto" data-id="element-114">
              <img src="https://cdn.magicpatterns.com/uploads/61GF88ynKGgzpUnazAfbJE/Diseno_sin_titulo_(17).png" alt="6 Volumes" className="w-full object-contain drop-shadow-2xl" data-id="element-115" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE VOLUMES */}
      <section className="py-16 bg-zinc-950 border-y border-zinc-900" data-id="element-116">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-117">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-118">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-119">
              What you get in <br data-id="element-120" />
              <span className="text-orange-500" data-id="element-121">KIT THE FOCUSED MINDSET™</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-122">
            {[{
            vol: '1',
            title: 'LITTLE EXPLORERS',
            age: 'Ages 3–4',
            desc: 'Visual recognition, fine motor control, and emotional awareness through pure play.',
            color: '#06b6d4'
          }, {
            vol: '2',
            title: 'BRAIN BUILDERS',
            age: 'Ages 5–6',
            desc: 'Sustained attention, working memory, and early logical thinking.',
            color: '#22c55e'
          }, {
            vol: '3',
            title: 'FOCUS WARRIORS',
            age: 'Ages 7–8',
            desc: 'Screens destroyed their attention span. This volume rebuilds it.',
            color: '#3b82f6'
          }, {
            vol: '4',
            title: 'LOGIC MASTERS',
            age: 'Ages 9–10',
            desc: 'Critical thinking, verbal reasoning, and cognitive flexibility.',
            color: '#a855f7'
          }, {
            vol: '5',
            title: 'MEMORY CHAMPIONS',
            age: 'Ages 8–11',
            desc: 'Studies hard but forgets fast. This volume fixes that.',
            color: '#f97316'
          }, {
            vol: '6',
            title: 'YOUNG GENIUSES',
            age: 'Ages 11–12',
            desc: 'Elite brain challenges for independent reasoning and leadership mindset.',
            color: '#ef4444'
          }].map((v, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="glass-panel rounded-2xl p-8 border-t-4 hover:-translate-y-1 transition-transform duration-300" style={{
            borderTopColor: v.color
          }} data-id="element-123">
                <div className="text-sm font-bold tracking-wider mb-2 uppercase text-[#F97316]" data-id="element-124">
                  Volume {v.vol} • {v.age}
                </div>
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-125">{v.title}</h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-126">{v.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-16 bg-[#09090b]" data-id="element-127">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-128">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-129">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 text-orange-500 px-4 py-1.5 rounded-full text-sm font-bold tracking-wider mb-4 uppercase" data-id="element-130">
              Free Bonuses
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-131">
              5 Exclusive Bonus Guides Included
            </h2>
            <p className="text-xl text-zinc-400" data-id="element-132">
              On top of the 6 volumes, you also receive these 5 bonus guides at
              no extra cost:
            </p>
          </motion.div>

          <div className="space-y-4" data-id="element-133">
            {[{
            num: '01',
            title: '30-Day Focus Planner',
            desc: 'Your complete 30-day brain training roadmap. Just print and follow.'
          }, {
            num: '02',
            title: 'Emotion Control for Kids',
            desc: 'Printable exercises to help children identify emotions and reset in 60 seconds.'
          }, {
            num: '03',
            title: 'Screen-Free Playbook',
            desc: 'Offline games and family interaction prompts for weekends and car rides.'
          }, {
            num: '04',
            title: 'Parent Quick-Start Guide',
            desc: 'Walks you through the full system in 15 minutes. Read once. Use forever.'
          }, {
            num: '05',
            title: 'Focus Rewards System',
            desc: 'Reward charts and badge progression that makes your child want to do the activities.'
          }].map((b, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex flex-col md:flex-row md:items-center justify-between bg-zinc-900/50 p-6 rounded-2xl border border-zinc-800" data-id="element-134">
                <div className="flex items-start md:items-center space-x-6 mb-4 md:mb-0" data-id="element-135">
                  <div className="text-3xl font-black text-zinc-800" data-id="element-136">
                    {b.num}
                  </div>
                  <div data-id="element-137">
                    <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1" data-id="element-138">
                      Bonus #{b.num}
                    </div>
                    <h4 className="text-xl font-bold text-white mb-1" data-id="element-139">
                      {b.title}
                    </h4>
                    <p className="text-zinc-400" data-id="element-140">{b.desc}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 md:text-right pl-14 md:pl-0" data-id="element-141">
                  <div className="text-zinc-500 line-through text-sm" data-id="element-142">
                    Value $17
                  </div>
                  <div className="font-bold text-lg uppercase tracking-wider text-[#F97316]" data-id="element-143">
                    FREE
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* OFFER / PRICING */}
      <section id="offer" className="py-16 bg-zinc-950 relative overflow-hidden" data-id="element-144">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(234,88,12,0.1),transparent_70%)] pointer-events-none" data-id="element-145" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-146">
          <motion.div {...fadeIn} className="text-center mb-12" data-id="element-147">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-148">
              Launch price ends soon
            </h2>
            <CountdownTimer data-id="element-149" />
            <p className="text-zinc-400 mt-6 text-lg" data-id="element-150">
              When the timer ends, the price returns to $97. Lock in the launch
              price today.
            </p>

            <div className="mt-8 max-w-md mx-auto" data-id="element-151">
              <div className="flex justify-between text-sm text-zinc-400 mb-2 font-medium" data-id="element-152">
                <span data-id="element-153">Launch-price spots</span>
                <span className="text-orange-500" data-id="element-154">88% claimed</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden" data-id="element-155">
                <div className="h-full bg-orange-500 w-[88%] rounded-full" data-id="element-156"></div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-zinc-900 border-2 border-orange-500 rounded-3xl p-8 md:p-12 shadow-[0_0_50px_rgba(234,88,12,0.15)] relative" data-id="element-157">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-orange-500 text-white px-6 py-2 rounded-full font-bold flex items-center space-x-2 shadow-lg" data-id="element-158">
              <Star className="w-4 h-4 fill-white" data-id="element-159" />
              <span data-id="element-160">Most Popular Choice</span>
            </div>

            <div className="text-center mb-10 mt-4" data-id="element-161">
              <div className="flex items-center justify-center gap-3 mb-2" data-id="element-162">
                <img src="https://cdn.magicpatterns.com/uploads/61GF88ynKGgzpUnazAfbJE/Diseno_sin_titulo_(17).png" alt="Kit The Focused Mindset" className="h-14 md:h-16 w-auto object-contain flex-shrink-0" data-id="element-163" />
                <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-164">
                  Kit The Focused Mindset™
                </h3>
              </div>
              <p className="text-xl text-orange-500 font-medium mb-4" data-id="element-165">
                6-Volume Box Set
              </p>
              <p className="text-zinc-400" data-id="element-166">
                For parents of kids ages 3–12 · Instant PDF · Print at home
              </p>
            </div>

            <div className="space-y-4 mb-10" data-id="element-167">
              {['6 Complete Brain Training Volumes (300+ printable activities)', 'Full progressive system — ages 3 to 12, done for you', '5 Bonus Guides Included (worth $85+)', 'Lifetime Access — pay once, keep forever', 'Print for all your children — no limits'].map((feature, i) => <div key={i} className="flex items-start space-x-3" data-id="element-168">
                  <CheckCircle2 className="w-6 h-6 text-orange-500 flex-shrink-0" data-id="element-169" />
                  <span className="text-lg text-zinc-200" data-id="element-170">{feature}</span>
                </div>)}
            </div>

            <div className="text-center mb-8 p-6 bg-zinc-950 rounded-2xl border border-zinc-800" data-id="element-171">
              <div className="flex items-center justify-center space-x-4 mb-2" data-id="element-172">
                <span className="text-3xl text-zinc-500 line-through font-medium" data-id="element-173">
                  $97
                </span>
                <span className="bg-red-500/20 text-red-500 px-3 py-1 rounded-full text-sm font-bold" data-id="element-174">
                  🔥 80% OFF
                </span>
              </div>
              <div className="text-6xl font-black text-white mb-2" data-id="element-175">
                $19{' '}
                <span className="text-2xl text-zinc-500 font-medium" data-id="element-176">USD</span>
              </div>
              <p className="text-green-500 font-medium" data-id="element-177">You save $78 today</p>
              <p className="text-sm text-zinc-500 mt-2 flex items-center justify-center" data-id="element-178">
                <CreditCard className="w-4 h-4 mr-1" data-id="element-179" /> One-time · Pay in your
                local currency
              </p>
            </div>

            <a href="https://thefocusedmindset.impultienda.ar/checkout" onClick={trackCheckout} className="w-full group relative inline-flex items-center justify-center px-8 py-5 font-bold text-white bg-orange-600 rounded-2xl overflow-hidden transition-all hover:scale-[1.02] hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-zinc-900 text-xl shadow-[0_0_40px_rgba(234,88,12,0.3)]" data-id="element-180">
              <span className="mr-2" data-id="element-181">🚀 YES, I WANT THE 6-VOLUME KIT</span>
            </a>

            <div className="mt-6 flex flex-col items-center space-y-3 text-sm text-zinc-400" data-id="element-182">
              <div className="flex items-center space-x-4" data-id="element-183">
                <span className="flex items-center" data-id="element-184">
                  <Zap className="w-4 h-4 mr-1 text-orange-500" data-id="element-185" /> Instant
                  access in 5 min
                </span>
                <span className="flex items-center" data-id="element-186">
                  <Lock className="w-4 h-4 mr-1 text-orange-500" data-id="element-187" /> Secure SSL
                  checkout
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FULL TESTIMONIALS */}
      <section className="py-16 bg-[#09090b] border-t border-zinc-900" data-id="element-188">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-189">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-190">
            <h2 className="text-3xl font-bold mb-4 uppercase tracking-wider text-zinc-400 text-sm" data-id="element-191">
              What parents who already have Kit The Focused Mindset™ say:
            </h2>
          </motion.div>

          <div className="space-y-6" data-id="element-192">
            {[{
            init: 'JM',
            name: 'Jessica M.',
            loc: 'Texas',
            role: 'Mom of a 7-year-old',
            text: 'After just 14 days, my son went from 3 minutes of focus to completing full 20-minute sessions on his own — and asking for more.'
          }, {
            init: 'AK',
            name: 'Amanda K.',
            loc: 'Florida',
            role: 'Mom of an 8-year-old',
            text: 'The Focus Warriors volume changed everything. She now pushes through every challenge. That shift in confidence is something no app could ever give her.'
          }, {
            init: 'RT',
            name: 'Rachel T.',
            loc: 'California',
            role: 'Mom of two ages 6 and 9',
            text: 'By day three my kids were reminding ME to print the activity. In 6 years of parenting I never experienced anything like that.'
          }, {
            init: 'MP',
            name: 'Michelle P.',
            loc: 'New York',
            role: 'Mom of a 9-year-old',
            text: 'We went from 45 minutes of iPad battles every night to 10 minutes of calm focused activity. Best purchase I made this entire year.'
          }, {
            init: 'SL',
            name: 'Sarah L.',
            loc: 'Ohio',
            role: 'Mom of a 6-year-old with ADHD',
            text: 'Nothing held his attention longer than 2 days. This is the first thing he asks for every morning. Day 19 and counting.'
          }, {
            init: 'TK',
            name: 'Tom K.',
            loc: 'Toronto',
            role: 'Dad of three kids',
            text: 'Everything is organized by age — I always know exactly which volume to use next.'
          }].map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="bg-zinc-900/50 p-6 md:p-8 rounded-2xl border border-zinc-800 flex flex-col md:flex-row gap-6" data-id="element-193">
                <div className="flex-shrink-0 flex items-center md:flex-col md:items-start gap-4 md:w-64" data-id="element-194">
                  <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center font-bold text-orange-500 text-lg" data-id="element-195">
                    {t.init}
                  </div>
                  <div data-id="element-196">
                    <div className="font-bold text-white" data-id="element-197">{t.name}</div>
                    <div className="text-sm text-zinc-500 flex items-center mt-1" data-id="element-198">
                      📍 {t.loc}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1" data-id="element-199">{t.role}</div>
                  </div>
                </div>
                <div className="flex-1" data-id="element-200">
                  <div className="flex items-center space-x-1 mb-3" data-id="element-201">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-orange-500 fill-orange-500" data-id="element-202" />)}
                  </div>
                  <p className="text-lg text-zinc-300 leading-relaxed" data-id="element-203">
                    "{t.text}"
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 bg-zinc-950" data-id="element-204">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-205">
          <motion.div {...fadeIn} className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 p-8 md:p-16 rounded-3xl text-center relative overflow-hidden" data-id="element-206">
            <ShieldCheck className="w-24 h-24 text-orange-500/20 absolute -top-4 -right-4" data-id="element-207" />

            <ShieldCheck className="w-16 h-16 text-orange-500 mx-auto mb-6 relative z-10" data-id="element-208" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 relative z-10" data-id="element-209">
              🛡️ TOTAL GUARANTEE — 7 DAYS
            </h2>

            <div className="space-y-6 text-lg text-zinc-300 relative z-10 max-w-2xl mx-auto" data-id="element-210">
              <p data-id="element-211">
                Download everything. Print the activities. Try them with your
                child.
              </p>
              <p className="font-medium text-white" data-id="element-212">
                If for any reason Kit The Focused Mindset™ doesn't improve your
                child's focus or make your evenings easier — we refund you 100%.
                No questions asked.
              </p>
              <p className="text-zinc-400 text-base" data-id="element-213">
                The confidence we have in this system is what lets us offer you
                this.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#09090b] border-t border-zinc-900" data-id="element-214">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-215">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-216">
            <h2 className="text-3xl md:text-4xl font-bold" data-id="element-217">
              ❓ FREQUENTLY ASKED QUESTIONS
            </h2>
          </motion.div>

          <div className="space-y-2" data-id="element-218">
            {[{
            q: '1 — Is this a physical product?',
            a: 'No. 100% digital. Download link via email after purchase. Start printing within minutes.'
          }, {
            q: '2 — What age is this designed for?',
            a: 'Ages 3 to 12. 6 volumes each built for a specific developmental stage. One purchase covers every level as they grow.'
          }, {
            q: '3 — How do I receive access?',
            a: 'Immediately after purchase you receive your download link by email. No waiting. No shipping.'
          }, {
            q: '4 — Do I need teaching experience?',
            a: 'Not at all. Every activity has clear instructions. The Quick-Start Guide walks you through everything in 15 minutes.'
          }, {
            q: '5 — Are all 5 bonuses included?',
            a: 'Yes. All 5 bonus guides are included in every purchase at no extra cost.'
          }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-219" />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA & FOOTER */}
      <footer className="bg-zinc-950 pt-16 pb-12 border-t border-zinc-900 relative" data-id="element-220">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-221">
          <div className="inline-flex items-center space-x-2 mb-8" data-id="element-222">
            <Brain className="w-6 h-6 text-orange-500" data-id="element-223" />
            <span className="text-xl font-bold tracking-widest text-zinc-100 uppercase" data-id="element-224">
              Focused Mindset
            </span>
          </div>

          <p className="text-2xl md:text-3xl font-medium text-white mb-8 leading-relaxed" data-id="element-225">
            Tonight, you could spend another evening watching your child
            disappear into a screen — while their childhood quietly slips by.
          </p>
          <p className="text-xl text-zinc-400 mb-12" data-id="element-226">
            Or you could spend 10 minutes doing something that actually builds
            their brain, their focus, and their confidence —{' '}
            <span className="text-orange-500 font-medium" data-id="element-227">
              starting right now.
            </span>
          </p>

          <div className="mb-12" data-id="element-228">
            <CountdownTimer data-id="element-229" />
          </div>

          <a href="https://thefocusedmindset.impultienda.ar/checkout" onClick={trackCheckout} className="group relative inline-flex items-center justify-center px-8 py-5 font-bold text-white bg-orange-600 rounded-full overflow-hidden transition-all hover:scale-105 hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-zinc-900 text-xl w-full md:w-auto shadow-[0_0_40px_rgba(234,88,12,0.4)] mb-8" data-id="element-230">
            <span className="mr-2" data-id="element-231">⚡ THE ULTIMATE FAMILY BUNDLE $19</span>
            <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-232" />
          </a>

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3 text-sm text-zinc-500 mb-24" data-id="element-233">
            <span className="flex items-center text-[#FBFBFE]" data-id="element-234">
              <Lock className="w-4 h-4 mr-1" data-id="element-235" /> Secure Payment
            </span>
            <span className="flex items-center text-[#FBFBFE]" data-id="element-236">
              <Zap className="w-4 h-4 mr-1" data-id="element-237" /> Access in 5 minutes
            </span>
            <span className="flex items-center text-[#FBFBFE]" data-id="element-238">
              <ShieldCheck className="w-4 h-4 mr-1" data-id="element-239" /> 7-Day Guarantee
            </span>
            <span className="flex items-center text-[#FBFBFE]" data-id="element-240">
              <CreditCard className="w-4 h-4 mr-1" data-id="element-241" /> Card | PayPal | Stripe
            </span>
          </div>

          <div className="border-t border-zinc-900 pt-12 text-left md:text-center" data-id="element-242">
            <div className="inline-flex items-center space-x-2 mb-6 opacity-50" data-id="element-243">
              <Brain className="w-5 h-5 text-zinc-400" data-id="element-244" />
              <span className="text-lg font-bold tracking-widest uppercase text-[#AAAAAB]" data-id="element-245">
                Focused Mindset
              </span>
            </div>
            <p className="text-zinc-500 text-sm max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-246">
              The "done-for-you" brain training system for parents who want
              their child to build real focus, memory, and self-control —
              without screens, without arguments, and without spending hours
              planning.
              <br data-id="element-247" />
              <br data-id="element-248" />
              Because raising a focused, confident child should not cost you
              years of trial and error.
            </p>
            <div className="text-zinc-600 text-xs flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4" data-id="element-249">
              <span data-id="element-250">© 2026 EurekaLabs.store™ — All Rights Reserved.</span>
              <span className="hidden md:inline" data-id="element-251">•</span>
              <span data-id="element-252">🌐 www.eurekalabs.store</span>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}